import { inputToLiters, tankOccupancy } from "../../services/tankUnit";
import {
  AGING_SUBSTATES,
  nextOperationByLot,
  paginate,
  startedOperationsByLot,
} from "../LotList/lotRows";

// Pure logic behind the tanks list: phase of the lot inside, occupancy, filters, summary, operations.

// Phases of the "Fase" select, in life-cycle order (same as the lots, without "closed": a tank never holds one).
export const PHASES = ["GRAPE", "MUST", "WINE", "AGING", "BOTTLED"];
export const STATUSES = ["IN_USE", "EMPTY", "FULL"];

export { paginate };

const quantityOf = (tank) => Number(tank?.quantity) || 0;
const capacityOf = (tank) => Number(tank?.capacity) || 0;

// Phase of the lot in the tank (null when empty); a wine that is aging is AGING, not WINE.
export const getTankPhase = (tank) => {
  if (!tank?.state) return null;
  if (
    tank.state === "WINE" &&
    AGING_SUBSTATES.includes(tank?.tank_substate?.substate)
  )
    return "AGING";
  return tank.state;
};

export const getTankStatus = (tank) => {
  const quantity = quantityOf(tank);
  if (quantity <= 0) return "EMPTY";
  return capacityOf(tank) > 0 && quantity >= capacityOf(tank)
    ? "FULL"
    : "IN_USE";
};

// percent is the real one (a tank can be over its capacity); bar is what the meter draws.
export const getTankFill = (tank) => {
  const percent = tankOccupancy(quantityOf(tank), capacityOf(tank));
  return { percent, bar: Math.min(Math.max(percent, 0), 100) };
};

export const summarizeTanks = (tanks) => {
  const capacity = (tanks || []).reduce((sum, t) => sum + capacityOf(t), 0);
  const quantity = (tanks || []).reduce((sum, t) => sum + quantityOf(t), 0);
  return {
    count: (tanks || []).length,
    capacity,
    quantity,
    percent: tankOccupancy(quantity, capacity),
  };
};

export const countByPhase = (tanks) => {
  const counts = {
    ALL: 0,
    ...Object.fromEntries(PHASES.map((phase) => [phase, 0])),
  };
  (tanks || []).forEach((tank) => {
    counts.ALL += 1;
    const phase = getTankPhase(tank);
    if (phase in counts) counts[phase] += 1;
  });
  return counts;
};

const normalize = (text) =>
  String(text ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const searchText = (tank, getLabel) =>
  normalize(
    [
      tank?.name,
      tank?.batch_name,
      tank?.batch_label,
      getLabel(`tankType${tank?.type}`),
    ].join(" "),
  );

const isSet = (value) => value !== undefined && value !== "" && value !== null;

// filters: { search, type, phase, status, color, substate, sanitized, capacityMin, capacityMax }; empty = no filter.
// sanitized: "OK" or "NOT_OK", for the empty tanks (a tank with wine in it is neither).
// The capacity limits are typed in the unit the user sees ("L" or "hL").
export const filterTanks = (
  tanks,
  filters = {},
  { getLabel = (key) => key, unit = "L" } = {},
) => {
  const {
    search,
    type,
    phase,
    status,
    color,
    substate,
    sanitized,
    capacityMin,
    capacityMax,
  } = filters;
  const needle = normalize(search).trim();

  return (tanks || []).filter((tank) => {
    if (type && tank?.type !== type) return false;
    if (phase && phase !== "ALL" && getTankPhase(tank) !== phase) return false;
    if (status && getTankStatus(tank) !== status) return false;
    if (color && tank?.color !== color) return false;
    if (substate && tank?.tank_substate?.substate !== substate) return false;
    if (sanitized) {
      if (getTankStatus(tank) !== "EMPTY") return false;
      if (Boolean(tank?.is_sanitized) !== (sanitized === "OK")) return false;
    }
    if (
      isSet(capacityMin) &&
      capacityOf(tank) < inputToLiters(capacityMin, unit)
    )
      return false;
    if (
      isSet(capacityMax) &&
      capacityOf(tank) > inputToLiters(capacityMax, unit)
    )
      return false;
    if (needle && !searchText(tank, getLabel).includes(needle)) return false;
    return true;
  });
};

// Map tankId -> most urgent open operation touching that tank (a USER only sees their own operations).
export const nextOperationByTank = (args) =>
  nextOperationByLot({ ...args, idField: "tank_id" });

// Map tankId -> started, not completed operations on that tank.
export const startedOperationsByTank = (args) =>
  startedOperationsByLot({ ...args, idField: "tank_id" });
